import React from "react";

// Define strict types to ensure TabContent can only be placed inside Tabs
export type TabContentElement = React.ReactElement<TabContentProps>;

interface TabContentProps {
	title: string;
	onVisible?: () => void;
	className?: string;
	children: React.ReactNode;
}

// TabContent component - designed to be used only inside Tabs
export const TabContent: React.FC<TabContentProps> = ({ children }) => {
	// The actual rendering is handled by the parent Tabs component
	return <>{children}</>;
};

interface TabsProps {
	children: TabContentElement | TabContentElement[];
	defaultActiveTab?: string;
	className?: string;
}

// Tabs component - only accepts TabContent as children
export const Tabs: React.FC<TabsProps> = ({
	className = "bg-primary-accent rounded-t-lg p-4",
	children,
	defaultActiveTab
}) => {
	// Validate children are TabContent components
	React.Children.forEach(children, (child) => {
		if (React.isValidElement(child)) {
			const typeString = (child.type as any).name || child.type;
			const source = (child as any)._source;

			//And - !== Not TabContent
			if (source && typeString !== "TabContent") {
				throw new Error(
					`Tabs component only accepts TabContent as children. Child:[${typeString}]@ ${source?.fileName}(ln:${source?.lineNumber}, col:${source?.columnNumber}) \n\t Recommend Changing to <TabContent>...</TabContent>`
				);
			}
		}
	});

	// Convert to array for easier handling
	const tabChildren = React.Children.toArray(children) as TabContentElement[];

	// Extract tab titles
	const tabTitles = tabChildren.map((child) => child.props.title);

	// Set initial active tab
	const initialTabIndex = defaultActiveTab
		? tabTitles.indexOf(defaultActiveTab)
		: 0;

	const [activeTabIndex, setActiveTabIndex] = React.useState(initialTabIndex);

	// Handle tab click
	const handleTabClick = (index: number) => {
		setActiveTabIndex(index);
		// Call onVisible callback when tab becomes visible
		//TODO: This should be a Async Promise style thing
		//TODO: When tab is clicked, the animation of the tabContents should slide in the same direction of the tab?
		const onVisible = tabChildren[index]?.props.onVisible;
		if (onVisible) {
			onVisible();
		}
	};

	// Initial onVisible call for default tab
	React.useEffect(() => {
		const onVisible = tabChildren[activeTabIndex]?.props.onVisible;
		if (onVisible) {
			onVisible();
		}
		// eslint-disable-next-line
	}, []); // Empty dependency array ensures this runs only once on mount

	return (
		<div className="">
			{/* Tab Headers - Match the design from the image */}
			<div className={`flex space-x-4 overflow-x-auto ${className}`}>
				{tabTitles.map((title, index) => (
					<button
						key={index}
						onClick={() => handleTabClick(index)}
						className={`px-6 py-2 text-primary rounded-full transition-colors whitespace-nowrap shrink-0 ${
							activeTabIndex === index
								? "bg-primary-background border border-primary-active"
								: ""
						}`}
					>
						{title}
					</button>
				))}
			</div>

			{/* Active Tab Content */}
			<div
				key={`${activeTabIndex}`}
				className={`${tabChildren[activeTabIndex].props.className}`}
			>
				{tabChildren[activeTabIndex]}
			</div>
		</div>
	);
};
